/**
 * Shared helpers for dropdown option label/value + alphabetical sort.
 */

export function defaultOptionLabel(option) {
  if (option == null) return '';
  if (typeof option !== 'object') return String(option);
  return (
    option.label ||
    option.value ||
    option.name ||
    option.companyName ||
    option.counselorName ||
    option.batchName ||
    option.courseName ||
    option.categoryName ||
    option.domainName ||
    option.fullName ||
    `${option.firstName || ''} ${option.lastName || ''}`.trim() ||
    String(option.id ?? '')
  );
}

export function defaultOptionValue(option) {
  if (option == null) return '';
  if (typeof option !== 'object') return option;
  if (option.id !== undefined && option.id !== null) return option.id;
  if (option.value !== undefined && option.value !== null) return option.value;
  return '';
}

/**
 * Normalize options to { value, label, raw } and sort A–Z by label.
 * Keeps empty/placeholder out — caller adds that separately.
 */
export function normalizeAndSortOptions(
  options = [],
  {
    getOptionLabel = defaultOptionLabel,
    getOptionValue = defaultOptionValue,
    getOptionListLabel,
    getOptionGroup,
  } = {}
) {
  const mapped = (Array.isArray(options) ? options : [])
    .map((option, index) => {
      const value = getOptionValue(option);
      const label = getOptionLabel(option);
      if (value === undefined || value === null || value === '') return null;
      const fullLabel = String(label || value);
      const listLabel =
        typeof getOptionListLabel === 'function'
          ? String(getOptionListLabel(option) || fullLabel)
          : fullLabel;
      const group =
        typeof getOptionGroup === 'function' ? String(getOptionGroup(option) || '') : '';
      return {
        value: String(value),
        label: fullLabel,
        listLabel,
        group,
        raw: option,
        index,
      };
    })
    .filter(Boolean);

  mapped.sort((a, b) => {
    const groupOrder = (a.group || '').localeCompare(b.group || '', undefined, {
      sensitivity: 'base',
      numeric: true,
    });
    if (groupOrder !== 0) return groupOrder;
    return a.label.localeCompare(b.label, undefined, { sensitivity: 'base', numeric: true });
  });
  return mapped;
}

export function filterOptionsByQuery(options, query) {
  const q = String(query || '')
    .trim()
    .toLowerCase();
  if (!q) return options;
  return options.filter((opt) => {
    const haystack = [opt.label, opt.listLabel, opt.group]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(q);
  });
}
